import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock, Check, X, Loader2 } from "lucide-react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

import { API_BASE } from "@/services/api";

/**
 * Password Setup Prompt
 * 
 * Shows after first OTP login to encourage users to set a password
 * for account stability and faster future logins.
 */
const PasswordSetupPrompt = ({ open, onClose }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;':,.<>?]/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isValid = hasMinLength && hasNumber && hasSpecial && passwordsMatch;

  const handleSetPassword = async () => {
    if (!isValid) return;
    
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE}/auth/password/set`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      localStorage.removeItem("show_password_prompt");
      toast({ description: "Password set successfully! You can now login with email + password." });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to set password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE}/auth/password/skip-prompt`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.removeItem("show_password_prompt");
    } catch (err) {
      // Ignore error, just close
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Modal - slides up from bottom on mobile */}
      <div className="relative bg-white w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300 safe-area-bottom">
        {/* Mobile drag indicator */}
        <div className="sm:hidden w-10 h-1 bg-slate-300 rounded-full mx-auto mt-2" />
        
        {/* Close button */}
        <button 
          onClick={handleSkip}
          className="absolute top-2 sm:top-3 right-2 sm:right-3 p-2 rounded-full hover:bg-slate-100 transition-colors z-10"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        <div className="p-4 sm:p-5 md:p-6 pb-6 sm:pb-6">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <Shield className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Secure your account</h2>
            <p className="text-sm text-slate-500 mt-1">
              Set a password for recovery and faster logins
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="Create a strong password"
                className="mt-1.5 h-12"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                placeholder="Confirm your password"
                className="mt-1.5 h-12"
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-medium text-slate-500 mb-2">Password requirements:</p>
              <div className="grid grid-cols-2 gap-2">
                <RequirementItem met={hasMinLength} text="8+ characters" />
                <RequirementItem met={hasNumber} text="One number" />
                <RequirementItem met={hasSpecial} text="Special char" />
                <RequirementItem met={passwordsMatch} text="Passwords match" />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}
          </div>

          {/* Actions */}
          <div className="mt-5 space-y-2">
            <Button
              onClick={handleSetPassword}
              disabled={!isValid || loading}
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> Set Password</>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="w-full h-11 text-slate-500"
              disabled={loading}
            >
              Skip for now
            </Button>
          </div>

          <p className="text-xs text-center text-slate-400 mt-3">
            You can set a password later from Account Settings
          </p>
        </div>
      </div>
    </div>
  );
};

const RequirementItem = ({ met, text }) => (
  <div className={`flex items-center gap-1.5 text-xs ${met ? "text-green-600" : "text-slate-400"}`}>
    {met ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
    <span>{text}</span>
  </div>
);

export default PasswordSetupPrompt;
